/**
 * @module components/search
 *
 * A keyboard-first search overlay across every piece of content on the site.
 *
 * The index is built from the data modules at boot, so adding an insight or a
 * phase automatically makes it findable — no separate index to maintain.
 *
 * Accessibility contract:
 *  - `role="dialog"` + `aria-modal="true"`, labelled by its search field.
 *  - Focus moves into the field on open and returns to the trigger on close.
 *  - `⌘K` / `Ctrl+K` opens, `Escape` closes, arrows move, `Enter` follows.
 *  - The result count is announced politely.
 */

import { PHASES, REQUIREMENTS_HUB } from "../data/phases.js";
import { CAPABILITIES } from "../data/capabilities.js";
import { INSIGHTS } from "../data/insights.js";
import { TRENDS } from "../data/trends.js";
import { CASES } from "../data/cases.js";
import { SERVICES } from "../data/services.js";
import { AI_STACK, AI_DOTS, AGENT_ROLES, AI_GUARDRAILS } from "../data/aiLayer.js";
import { NAV } from "../data/site.js";
import { qs, on, setAttributes } from "../core/dom.js";
import { icon, escapeHtml } from "../svg/icons.js";

/**
 * @typedef {Object} SearchEntry
 * @property {string} kind     Display category.
 * @property {string} title
 * @property {string} text     Text searched against.
 * @property {string} href     Where activating the entry navigates.
 * @property {string} [hint]   Secondary line shown on the right.
 */

/**
 * Build the searchable index from the content modules.
 *
 * @returns {SearchEntry[]}
 */
export function buildSearchIndex() {
  return [
    ...NAV.map((item) => ({
      kind: "Section",
      title: item.label,
      text: `${item.label} ${item.id} section`,
      href: `#${item.id}`,
    })),
    ...PHASES.map((phase) => ({
      kind: "ADM phase",
      title: `${phase.code} · ${phase.name}`,
      text: [
        phase.name,
        phase.tagline,
        phase.summary,
        phase.narrative,
        phase.focus,
        phase.qualityGate,
        phase.activities.join(" "),
        phase.deliverables.join(" "),
        phase.ai.headline,
        phase.ai.body,
        phase.ai.patterns.map((pattern) => `${pattern.title} ${pattern.body} ${pattern.agent ?? ""}`).join(" "),
        phase.capabilities.join(" "),
      ].join(" "),
      href: "#adm",
      hint: `Phase ${phase.code}`,
    })),
    {
      kind: "Hub",
      title: REQUIREMENTS_HUB.name,
      text: `${REQUIREMENTS_HUB.name} ${REQUIREMENTS_HUB.tagline} ${REQUIREMENTS_HUB.narrative} ${REQUIREMENTS_HUB.activities.join(" ")} requirements traceability`,
      href: "#adm",
      hint: "The centre of the ADM",
    },
    ...AI_STACK.map((layer) => ({
      kind: "AI layer",
      title: layer.name,
      text: `${layer.name} ${layer.body} ${layer.components.join(" ")} stack`,
      href: "#ai-layer",
      hint: `Layer ${layer.index}`,
    })),
    ...AI_DOTS.map((dot) => ({
      kind: "AI dot",
      title: dot.label,
      text: `${dot.label} ${dot.body} ${dot.where} intelligence`,
      href: "#ai-layer",
      hint: dot.where,
    })),
    ...AGENT_ROLES.map((agent) => ({
      kind: "Agent",
      title: agent.name,
      text: `${agent.name} ${agent.mandate} ${agent.tools.join(" ")} ${agent.boundary} agentic`,
      href: "#ai-layer",
      hint: "Agentic workforce",
    })),
    ...AI_GUARDRAILS.map((guardrail) => ({
      kind: "Guardrail",
      title: guardrail.name,
      text: `${guardrail.name} ${guardrail.body} guardrail assurance`,
      href: "#ai-layer",
      hint: "Guardrails",
    })),
    ...CAPABILITIES.map((capability) => ({
      kind: "Capability",
      title: capability.name,
      text: `${capability.name} ${capability.tagline} ${capability.body} ${capability.outcome} ${capability.group}`,
      href: "#capabilities",
      hint: "Capabilities",
    })),
    ...INSIGHTS.map((insight) => ({
      kind: insight.type,
      title: insight.title,
      text: `${insight.title} ${insight.abstract} ${insight.topic} ${insight.tags.join(" ")}`,
      href: "#insights",
      hint: insight.topic,
    })),
    ...TRENDS.map((trend) => ({
      kind: "Trend",
      title: trend.name,
      text: `${trend.name} ${trend.body} ${trend.sowhat} ${trend.quadrant} ${trend.ring}`,
      href: "#horizon",
      hint: trend.ring,
    })),
    ...CASES.map((entry) => ({
      kind: "Case study",
      title: entry.title,
      text: `${entry.title} ${entry.summary} ${entry.situation} ${entry.intervention} ${entry.outcome} ${entry.sector} ${entry.client}`,
      href: "#proof",
      hint: entry.sector,
    })),
    ...SERVICES.map((service) => ({
      kind: "Service",
      title: service.name,
      text: `${service.name} ${service.tagline} ${service.body} ${service.outcome} ${service.includes.join(" ")}`,
      href: "#services",
      hint: service.shape.split(",")[0],
    })),
  ];
}

/**
 * Rank entries against a query.
 *
 * A deliberately simple, predictable scorer: title matches win, then tag-ish
 * matches, then body matches. Predictability matters more than sophistication
 * at this scale.
 *
 * @param {SearchEntry[]} index
 * @param {string} query
 * @param {number} [limit=12]
 * @returns {Array<{ entry: SearchEntry, score: number }>}
 */
export function searchIndex(index, query, limit = 12) {
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .filter((term) => term.length > 1);

  if (!terms.length) return [];

  const scored = [];
  for (const entry of index) {
    const title = entry.title.toLowerCase();
    const text = entry.text.toLowerCase();
    let score = 0;

    for (const term of terms) {
      if (title.startsWith(term)) score += 12;
      else if (title.includes(term)) score += 8;
      else if (text.includes(term)) score += 3;
      else if (entry.kind.toLowerCase().includes(term)) score += 5;
      else {
        score = -Infinity;
        break;
      }
    }

    if (score > 0) scored.push({ entry, score });
  }

  return scored.sort((a, b) => b.score - a.score).slice(0, limit);
}

/**
 * Initialise the search overlay.
 *
 * @param {Object} [options]
 * @param {(target: string) => void} [options.onNavigate]
 * @returns {() => void} Teardown.
 */
export function initSearch(options = {}) {
  const root = qs("[data-search]");
  if (!root) return () => {};

  const field = qs("[data-search-input]", root);
  const list = qs("[data-search-results]", root);
  const status = qs("[data-search-status]", root);
  const triggers = Array.from(document.querySelectorAll("[data-search-open]"));
  if (!field || !list) return () => {};

  const index = buildSearchIndex();
  const disposers = [];

  /** @type {number} */
  let active = -1;
  /** @type {Array<{ entry: SearchEntry, score: number }>} */
  let results = [];
  /** @type {Element|null} */
  let lastFocus = null;

  const isOpen = () => root.classList.contains("is-open");

  const navigate = (href) => {
    const id = href.startsWith("#") ? href.slice(1) : null;
    if (options.onNavigate) {
      options.onNavigate(href);
    } else if (id) {
      const target = document.getElementById(id);
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const renderResults = () => {
    if (!results.length) {
      const query = field.value.trim();
      list.innerHTML = query
        ? '<p class="search-empty">No matches. Try “governance”, “agent”, “data contract” or “TOGAF”.</p>'
        : '<p class="search-empty">Search phases, capabilities, insights, trends, case studies and services.</p>';
      if (status) status.textContent = "No results";
      return;
    }

    list.innerHTML = results
      .map(
        ({ entry }, i) => `
        <button type="button" class="search-result${i === active ? " is-active" : ""}"
                data-search-result="${i}" role="option" aria-selected="${i === active}">
          <span class="search-result__kind">${escapeHtml(entry.kind)}</span>
          <span class="search-result__title">${escapeHtml(entry.title)}</span>
          <span class="search-result__hint">${escapeHtml(entry.hint ?? "")}</span>
        </button>`
      )
      .join("");

    if (status) {
      status.textContent = `${results.length} result${results.length === 1 ? "" : "s"}`;
    }
  };

  const update = () => {
    results = searchIndex(index, field.value);
    active = results.length ? 0 : -1;
    renderResults();
  };

  const open = (trigger) => {
    lastFocus = trigger ?? document.activeElement;
    root.classList.add("is-open");
    root.removeAttribute("hidden");
    setAttributes(root, { "aria-hidden": "false" });
    document.documentElement.style.overflow = "hidden";
    update();
    // Focus after the transition starts so the caret lands reliably.
    requestAnimationFrame(() => field.focus());
  };

  const close = () => {
    if (!isOpen()) return;
    root.classList.remove("is-open");
    setAttributes(root, { "aria-hidden": "true" });
    window.setTimeout(() => {
      if (!isOpen()) root.setAttribute("hidden", "");
    }, 260);
    document.documentElement.style.overflow = "";
    field.value = "";
    if (lastFocus instanceof HTMLElement) lastFocus.focus();
  };

  const move = (delta) => {
    if (!results.length) return;
    active = (active + delta + results.length) % results.length;
    renderResults();
    list.querySelector(`[data-search-result="${active}"]`)?.scrollIntoView({ block: "nearest" });
  };

  const activate = () => {
    const chosen = results[active];
    if (!chosen) return;
    close();
    navigate(chosen.entry.href);
  };

  /* --- Wiring ----------------------------------------------------------- */

  triggers.forEach((trigger) =>
    disposers.push(on(trigger, "click", () => open(trigger)))
  );

  disposers.push(
    on(document, "keydown", (event) => {
      const isCombo = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";
      if (isCombo) {
        event.preventDefault();
        isOpen() ? close() : open();
        return;
      }
      if (!isOpen()) return;

      if (event.key === "Escape") {
        event.preventDefault();
        close();
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        move(1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        move(-1);
      } else if (event.key === "Enter") {
        event.preventDefault();
        activate();
      }
    })
  );

  disposers.push(on(field, "input", update));

  disposers.push(
    on(list, "click", (event) => {
      const button = event.target instanceof Element ? event.target.closest("[data-search-result]") : null;
      if (!button) return;
      active = Number(button.getAttribute("data-search-result"));
      activate();
    })
  );

  // Backdrop click closes.
  disposers.push(
    on(root, "mousedown", (event) => {
      if (event.target === root) close();
    })
  );

  root.setAttribute("hidden", "");
  setAttributes(root, { "aria-hidden": "true" });

  // Decorative search glyph inside the field.
  const glyph = qs("[data-search-glyph]", root);
  if (glyph) glyph.innerHTML = icon("search", { size: 18 });

  return () => {
    disposers.forEach((dispose) => dispose());
    document.documentElement.style.overflow = "";
  };
}
